import { useEffect, useMemo, useState } from 'react'

import { parseISO } from 'date-fns'
import { format, utcToZonedTime } from 'date-fns-tz'
import { get, isEmpty, omit } from 'lodash-es'

import {
  AvailabilitiesResponse,
  Program,
  PROVIDER_AVAILABILITY_ERROR_DETAIL,
  ProviderInfo,
  ProviderProgramTaxonomy,
} from '../../../models'
import { AppointmentClass } from '../../appointments/utils'
import { FOURTEEN_DAYS_TO_MS, SIXTY_DAYS_TO_MS } from '../../calendar/types'
import { DEFAULT_PROGRAM_TAXONOMY } from '../../common/constants/customerProgram'
import { convertProviderTreatmentModalitiesToDisplayedTreatmentOptions } from '../../common/constants/providerTreatmentModalityConstants'
import { TREATMENT_OPTIONS } from '../../common/constants/treatmentOptions'
import { isProviderAvailableForBooking } from '../../favoritedProviders/utils'

/**
 * Fetches Calendar Service availability for all favorited providers.
 * Favorited providers can be saved and favorited across various search contexts, like treatmentPreference or onboarding vs DBL.
 * To fetch availability for all providers, we need to group providers by shared program taxonomy, appointment class, and number of days to fetch as these params all dictate what's returned from CS.
 * This hook aggregates all favorited providers into groups based on the above criteria, and then fetches availability for each group in parallel.
 * Once all requests have completed, we combine the results into a single object and return it. We also parse the availability response to determine
 * whether or not to display certain CTAs or fast track booking buttons.
 */
export const useGetFavoritedProvidersAvailabilities = ({
  providers,
  programs,
  getProviderAvailabilityCalendarServicePromise,
  favoritedProvidersMetadataMap,
  recurringAppointmentClassProviders = [],
  clearParsedAvailabilitiesCache,
}: {
  providers: ProviderInfo[]
  programs: Program[]
  getProviderAvailabilityCalendarServicePromise: (
    providerIds: string[],
    params: {
      programTaxonomy: Omit<ProviderProgramTaxonomy, 'role'>
      appointmentClass: AppointmentClass
      numDaysToFetchMs: number
      appointmentDuration?: number
    },
  ) => Promise<AvailabilitiesResponse>
  favoritedProvidersMetadataMap: Record<string, any> | null
  recurringAppointmentClassProviders?: string[]
  clearParsedAvailabilitiesCache?: () => void
}) => {
  const [hasProcessedProviderAvailabilities, setHasProcessedProviderAvailabilities] = useState(false)
  const [isLoadingAvailabilities, setIsLoadingAvailabilities] = useState(true)

  /* used for displaying the provider results cards CTAs */
  const [transformedProviderAvailabilities, setTransformedProviderAvailabilities] = useState({})
  /* the aggregated raw responses coming back from calendar service for all providers */
  const [csResponseProviderAvailabilities, setCSResponseProviderAvailabilities] = useState({})

  // Group providers by treatment preference to prepare for bulk fetching availability
  const providersGroupedByTreatmentPreference = useMemo(() => {
    if (!providers || !favoritedProvidersMetadataMap) return {}

    return providers.reduce((groups, provider) => {
      // Skip providers that are not available for booking as they're disabled on the favorited providers page
      if (!isProviderAvailableForBooking({ provider })) return groups

      const providerMetadata = favoritedProvidersMetadataMap[provider.lyra_id]
      let treatmentPreference: string = 'unknown'

      // Check the metadata map for the treatment preference that the user selected when the provider was favorited
      if (providerMetadata?.treatmentPreference) {
        treatmentPreference = providerMetadata.treatmentPreference
      } else {
        // If treatment preference is not available, such as from direct booking links, use the provider's modalities to determine the treatment preference
        const modalities = provider.modalities || []
        const treatmentPreferencesFromProviderModalities =
          convertProviderTreatmentModalitiesToDisplayedTreatmentOptions(modalities)

        if (treatmentPreferencesFromProviderModalities.length > 0) {
          treatmentPreference = treatmentPreferencesFromProviderModalities[0].formValue
        }
      }

      // Create and append provider to assigned treatment preference group
      if (!groups[treatmentPreference]) {
        groups[treatmentPreference] = []
      }
      groups[treatmentPreference].push(provider)
      return groups
    }, {} as Record<string, ProviderInfo[]>)
  }, [providers, favoritedProvidersMetadataMap])

  useEffect(() => {
    setHasProcessedProviderAvailabilities(false)
    setIsLoadingAvailabilities(true)
  }, [providersGroupedByTreatmentPreference])

  useEffect(() => {
    if (hasProcessedProviderAvailabilities || isEmpty(providersGroupedByTreatmentPreference) || isEmpty(programs))
      return

    // From the user's customer programs, create a map of programs with provider_modality as the key. Certain treatmentPreferences map directly
    // to the named provider modality which saves us the time of figuring out the most appropriate taxonomy to fetch availability with
    const programsByProviderModality = programs.reduce((acc, program: Program) => {
      acc[program.provider_modality] = { ...program }
      return acc
    }, {} as { [key: string]: Program })

    // We'll group providers by shared { program taxonomy, appointment class, and number of days }.
    // The number of entries in this map is the number of unique requests to CS we'll need to make.
    const availabilityGroupsMap = new Map<
      string,
      {
        programTaxonomy: Omit<ProviderProgramTaxonomy, 'role'>
        providerIds: string[]
        appointmentClass: AppointmentClass
        numDaysToFetchMs: number
      }
    >()

    // For each group in providersGroupedByTreatmentPreference, further classify them by the most appropriate
    // program_taxonomy, appointmentClass, and number of days to fetch availability for.
    // Write to availabilityGroupsMap
    Object.entries(providersGroupedByTreatmentPreference).forEach(([treatmentPreference, providersInGroup]) => {
      if (isEmpty(providersInGroup)) return

      // Determine calendar window based on provider types in this group
      const isBCMOrCLEProviderGroup = [
        TREATMENT_OPTIONS.CLINICAL_LEAVE_EVALUATION as string,
        TREATMENT_OPTIONS.BLENDED_CARE_MEDS as string,
      ].includes(treatmentPreference)

      const numDaysToFetchMs = isBCMOrCLEProviderGroup ? SIXTY_DAYS_TO_MS : FOURTEEN_DAYS_TO_MS

      // If the treatment preference doesn't map to a provider modality, we'll use the default program taxonomy (BCT)
      // Falling back to the default taxonomy here follows the existing logic in the hook useProgramTaxonomy which fetches taxonomy based on treatment option.
      // This just adapts the logic to work for fetching availability for multiple providers in different search contexts
      if (!programsByProviderModality[treatmentPreference]) {
        const programTaxonomy = omit(DEFAULT_PROGRAM_TAXONOMY, 'role')
        providersInGroup.forEach((provider: ProviderInfo) => {
          // Check if any providers in the group are recurring appointment class, i.e. the user has previously completed an appointment with this provider
          const appointmentClass = recurringAppointmentClassProviders.includes(provider.lyra_id)
            ? AppointmentClass.RECURRING
            : AppointmentClass.INITIAL

          // Create a key with this unique taxonomy/appointment class/appointment window combo and add the provider to the group
          const availabilityGroupsMapKey = `${JSON.stringify(programTaxonomy)}-${appointmentClass}-${numDaysToFetchMs}`
          if (availabilityGroupsMap.has(availabilityGroupsMapKey)) {
            availabilityGroupsMap.get(availabilityGroupsMapKey)!.providerIds.push(provider.lyra_id)
          } else {
            availabilityGroupsMap.set(availabilityGroupsMapKey, {
              programTaxonomy: DEFAULT_PROGRAM_TAXONOMY,
              providerIds: [provider.lyra_id],
              appointmentClass,
              numDaysToFetchMs,
            })
          }
        })
      } else {
        // If the treatment preference does map to a provider modality, we'll use the program taxonomy from the associated customer program
        providersInGroup.forEach((provider: ProviderInfo) => {
          let program_taxonomy

          // Each provider may be assigned 1 or multiple taxonomies
          const programTaxonomyOptions: ProviderProgramTaxonomy[] = provider.program_taxonomy || []

          if (programTaxonomyOptions.length === 1) {
            program_taxonomy = programTaxonomyOptions[0]
          } else {
            // Find a program taxonomy assigned to the provider which matches one found in the matched customer program
            program_taxonomy =
              programsByProviderModality[treatmentPreference].program_taxonomy.find((pt) => {
                return programTaxonomyOptions.find(
                  (pt2) =>
                    pt2.clientele === pt.clientele &&
                    pt2.treatment === pt.treatment &&
                    pt2.partner === pt.partner &&
                    pt2.offering === pt.offering,
                )
              }) ?? DEFAULT_PROGRAM_TAXONOMY
          }

          const programTaxonomy = omit(program_taxonomy, 'role')
          // Again, check if any providers in the group are recurring appointment class.
          const appointmentClass = recurringAppointmentClassProviders.includes(provider.lyra_id)
            ? AppointmentClass.RECURRING
            : AppointmentClass.INITIAL

          // Create a key with this unique taxonomy/appointment class/appointment window combo and add the provider to the group
          const availabilityGroupsMapKey = `${JSON.stringify(programTaxonomy)}-${appointmentClass}-${numDaysToFetchMs}`
          if (availabilityGroupsMap.has(availabilityGroupsMapKey)) {
            availabilityGroupsMap.get(availabilityGroupsMapKey)!.providerIds.push(provider.lyra_id)
          } else {
            availabilityGroupsMap.set(availabilityGroupsMapKey, {
              programTaxonomy: programTaxonomy,
              providerIds: [provider.lyra_id],
              appointmentClass,
              numDaysToFetchMs,
            })
          }
        })
      }
    })

    // Create an array of CalendarService requests, one entry for each of our unique availability requests
    const availabilityRequestsPromises = Array.from(availabilityGroupsMap.values()).map((group) => {
      const { programTaxonomy, providerIds, appointmentClass, numDaysToFetchMs } = group
      return getProviderAvailabilityCalendarServicePromise(providerIds, {
        programTaxonomy,
        appointmentClass,
        numDaysToFetchMs,
      })
        .then((availabilitiesResponseData) => {
          // After fetching availability, we need to parse and format the data to populate the card components with fast track booking and other CTAs pending availability.
          const parsedAvailabilityForCards = transformAvailabilityForCards(availabilitiesResponseData)
          // We still want to keep the raw AvailabilitiesResponse data.
          return { availabilitiesResponseData, transformedData: parsedAvailabilityForCards, parsedAvailabilityForCards }
        })
        .catch((error) => {
          console.error(
            `Failed to fetch availability for providers: ${providerIds} in programTaxonomy ${programTaxonomy}`,
            error,
          )
          // Return an empty object in case of error
          return {}
        })
    })

    // Wait for all CalendarService availability requests to complete
    Promise.all(availabilityRequestsPromises)
      .then((availabilityRequestsResults) => {
        // Combine all transformed card availability results into a single object
        const combinedParsedAvailabilityForCards = availabilityRequestsResults.reduce((acc, result) => {
          return { ...acc, ...get(result, 'parsedAvailabilityForCards', {}) }
        }, {})

        // Combine all raw results into an object with provider_id as key
        const combinedAvailabilitiesResponseData = availabilityRequestsResults.reduce<{ [providerId: string]: any }>(
          (acc, result) => {
            const availabilityData = get(result, 'availabilitiesResponseData', { provider_availability: [] })
            // Create entries for each provider in the availability response
            availabilityData.provider_availability.forEach((providerAvailability) => {
              const providerId = providerAvailability.provider_id
              acc[providerId] = {
                ...providerAvailability,
              }
            })

            return acc
          },
          {} as { [providerId: string]: any },
        )

        // Update state with all availabilities
        setTransformedProviderAvailabilities(combinedParsedAvailabilityForCards)
        setCSResponseProviderAvailabilities(combinedAvailabilitiesResponseData)
        setHasProcessedProviderAvailabilities(true)
        setIsLoadingAvailabilities(false)
        // Clear any parsed availabilities cache since we've fetched updated availability data
        clearParsedAvailabilitiesCache?.()
      })
      .catch((error) => {
        // Break from parsing and loading provider availabilities to prevent the whole page from loading. The user will still see the page, but the
        // card CTAs will show contact to book instead as provider availability couldn't properly fetch.
        console.error('Error while fetching and parsing provider availabilities:', error)
        setHasProcessedProviderAvailabilities(true)
        setIsLoadingAvailabilities(false)
      })
  }, [
    providersGroupedByTreatmentPreference,
    hasProcessedProviderAvailabilities,
    programs,
    recurringAppointmentClassProviders,
    getProviderAvailabilityCalendarServicePromise,
    clearParsedAvailabilitiesCache,
  ])

  return { isLoadingAvailabilities, transformedProviderAvailabilities, csResponseProviderAvailabilities }
}

// Helper function to parse the first three availability slots from CalendarService for each provider, since we only display up to 3 fast track slots.
// Used to determine whether or not to display the fast track booking buttons for each provider on the favorited providers page.
const transformAvailabilityForCards = (availabilityResponse: AvailabilitiesResponse) => {
  const parsedResultsByProviderId: { [providerId: string]: any } = {}

  availabilityResponse.provider_availability.forEach((pa) => {
    const providerId = pa.provider_id

    // Non-CC provider (calendar not found by CS) -- show "Contact to Book"
    if (pa.error_detail === PROVIDER_AVAILABILITY_ERROR_DETAIL.NOT_FOUND) {
      parsedResultsByProviderId[providerId] = {
        availability: [],
        timezone: '',
        hasConnectedCalendar: false,
        interval: pa.appointment_duration_minutes,
        contactForAvailabilityDisabled: false,
        showAppointmentsDisabled: true,
        matchingScheduleSlotsString: [],
      }
      return
    }
    // CS returned error that wasn't a NOT_FOUND error -- show "This provider is not accepting new patients"
    // If a timezone isn't provided (null) or a provider has no availability for some reason, we also cannot reliably display any CTAs
    else if (pa.status === 'ERROR' || !pa.local_time_zone || !pa.availability?.length) {
      parsedResultsByProviderId[providerId] = {
        availability: [],
        timezone: '',
        hasConnectedCalendar: true, // They DO have a calendar, it's just broken/unreachable
        interval: pa.appointment_duration_minutes,
        contactForAvailabilityDisabled: false,
        showAppointmentsDisabled: true,
        matchingScheduleSlotsString: [],
      }
      return
    }

    // First 3 slots. We can disregard the remaining availability slots to significantly reduce the amount of data we need to parse.
    const firstThreeAvailabilitySlots = pa.availability.slice(0, 3)
    const transformedAvailabilityForProviderCards: { [date: string]: string[] } = {}

    firstThreeAvailabilitySlots.forEach((slot) => {
      const timeZone = pa.local_time_zone!
      const utcDate = parseISO(slot.start)
      const zonedDate = utcToZonedTime(utcDate, timeZone) // Convert the UTC date to the provider's timezone

      const dateStr = format(zonedDate, 'yyyy-MM-dd', { timeZone })
      const timeStr = format(zonedDate, 'HH:mm', { timeZone })

      // Add the time to the array for this date key, e.g. { '2024-01-01': ['09:00', '10:00', '11:00'] }
      if (!transformedAvailabilityForProviderCards[dateStr]) {
        transformedAvailabilityForProviderCards[dateStr] = []
      }
      transformedAvailabilityForProviderCards[dateStr].push(timeStr)
    })
    const availability = Object.entries(transformedAvailabilityForProviderCards).map(([date, times]) => ({
      date,
      times: times as string[],
    }))

    parsedResultsByProviderId[providerId] = {
      availability,
      timezone: pa.local_time_zone,
      hasConnectedCalendar: true,
      interval: pa.appointment_duration_minutes,
      contactForAvailabilityDisabled: false,
      showAppointmentsDisabled: false,
      matchingScheduleSlotsString: [],
    }
  })
  return parsedResultsByProviderId
}
